import React, { useState, useEffect } from "react";
import { getTemplates } from './getTemplates';
import { GetPrice, fetchDbPriceRules, clearDbPriceRulesCache } from "./getPrice";
import { fetchUserField } from '../utils/userSettings';
import { authFetch } from "../utils/api";
import { SHOW_DB_PRICE_AND_MATCH, PRICES_FROM_DB } from "../constants";
import { useFeatures } from '../utils/FeaturesContext';
import './DynamicTemplateRenderer.css';

const DynamicTemplateRenderer = ({ doorData, displayPrices = false, updateCalcData }) => {
  const { hasFeature } = useFeatures();

  const [templates, setTemplates] = useState([]);
  const lines = {}; // Shared structure

  const [alertDiscount, setAlertDiscount] = useState(0);
  const [upPrice, setUpPrice] = useState(0);
  const [QuotePromotions, setQuotePromotions] = useState([]);
  const [dbPriceRulesState, setDbPriceRulesState] = useState([]); // Store DB price rules
  
  useEffect(() => {
    const loadField = async () => {
      const fetchedUpPrice = await fetchUserField('upPrice');
      setUpPrice(fetchedUpPrice);
  
      const fetchedAlertDiscount = await fetchUserField('alertDiscount');
      setAlertDiscount(fetchedAlertDiscount);
    };
  
    loadField();
  }, []);

  useEffect(() => {
    const getAssignedQuotePromotions = async(id) => {
      try {
        const data = await authFetch(`/quotes/${id}`);
        setQuotePromotions(data?.crm_promotions);
      } catch (err) {
        console.error('Failed to fetch item quotes:', err);
      } finally {
        //nothing so far
      }  
    }
    if (doorData?.document?.quoteId){
      getAssignedQuotePromotions(doorData?.document?.quoteId);
    }

  }, [doorData]);
  
  
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await authFetch('/appearance-templates/');

        const fixedTemplates = (response || []).map(template => ({
          ...template,
          expressions: typeof template.expressions === 'string'
            ? JSON.parse(template.expressions)
            : (template.expressions || {})
        }))
        .filter(template => {
          const docType = doorData?.document?.type || 'windows';
          const productType = docType === 'patiodoors' ? 'patiodoors' : 'windows';
          return !template.product_type || template.product_type === productType;
        });

        setTemplates(fixedTemplates.sort((a, b) =>
          (a.order_place || 0) - (b.order_place || 0)
        ));
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        setTemplates([]);
      }
    };

    const loadTemplates = async () => {
      console.log("Template name:", doorData.quoteTemplate);
      const currentTemplate = await getTemplates(doorData.quoteTemplate);
      setTemplates(currentTemplate);
    };
    
    if (doorData.quoteTemplate === "w16_template" || doorData.quoteTemplate === "pd_template"){
      if (templates.length === 0){
        fetchTemplates();
      }
    }
    else{
      loadTemplates();
    }
  }, []);

  // Fetch DB price rules once when templates are loaded
  useEffect(() => {
    if (templates.length === 0) return;

    const fetchDbRules = async () => {
      try {
        clearDbPriceRulesCache(); // DEBUG: Clear cache to force fresh fetch
        const allDbPriceRules = await fetchDbPriceRules();

        // DEBUG: Filter to only specific tables for testing (set to 1 to enable filter, 0 to use all rules)
        let filteredRules;
        if (0) {
          filteredRules = allDbPriceRules.filter(r =>
            r.id === 'w1' ||
            r.id === 'w2' ||
            r.id === 'w3' ||
            r.id === 'w4' ||
            r.id === 'w5' ||
            r.id === 'top1_1' ||
            r.id === 'top1' ||
            r.id === 'top2' ||
            r.id === 'top3' ||
            r.id === 'top4' ||
            r.id === 'top5' ||
            r.id === 'topArch' ||

            r.id === 'brickmoldSize' ||
            r.id === 'ref_brickmoldSize' ||

            r.id === 'GlassEnergy' ||
            r.id === 'ref_GlassEnergy' ||
            r.id === 'GlassStrength' ||
            r.id === 'ref_GlassStrength' ||
            r.id === 'GlazingType' ||
            r.id === 'ref_GlazingType' ||
            r.id === 'NeatGlass' ||
            r.id === 'ref_NeatGlass' ||
            r.id === 'jambType' ||
            r.id === 'ref_jambType' ||
            r.id === 'interiorCasing'||
            r.id === 'ref_interiorCasing' ||


            r.id === 'ref_rangeWindowPricesWhite' ||
            r.id === 'ref_rangeWindowPricesColor' ||
            r.id === 'ref_rangeWindowPricesDoubleColor' ||
            r.id === 'ref_ShapePrice' ||
            r.id === 'w1_grillType' ||
            r.id === 'w2_grillType' ||
            r.id === 'w3_grillType' ||
            r.id === 'w4_grillType' ||
            r.id === 'w5_grillType' ||
            r.id === 'top1_grillType' ||
            r.id === 'top2_grillType' ||
            r.id === 'top3_grillType'||
            r.id === 'top4_grillType' ||
            r.id === 'top5_grillType' ||
            r.id === 'archGrillType'
          );
        } else {
          filteredRules = allDbPriceRules; // Use all rules without filtering
        }
        // Filter rules by product type (windows/patiodoors)
        const docType = doorData?.document?.type || 'windows';
        const productType = docType === 'patiodoors' ? 'patiodoors' : 'windows';
        const productFilteredRules = filteredRules.filter(r =>
          !r.applies_to || r.applies_to === 'all' || r.applies_to === productType
        );
        setDbPriceRulesState(productFilteredRules);
      } catch (err) {
        console.warn("Failed to fetch DB pricing rules:", err);
      }
    };

    fetchDbRules();
  }, [templates.length]);

  const renderTemplate = ({ id, condition, template, expressions, order_place }) => {
    try {
      const isVisible = new Function("doorData", `return (${condition})`)(doorData);

      // Prepare evaluated values
      const evaluatedValues = {};
      for (const key in expressions) {
        try {
          evaluatedValues[key] = new Function("doorData", `return ${expressions[key]}`)(doorData);
        } catch (exprErr) {
          console.error(`[ERROR] Expression failed for ${id}.${key}:`, expressions[key], exprErr);
          evaluatedValues[key] = '';
        }
      }

      // Replace all <<key>> with evaluated values
      let finalHTML = template;
      for (const key in evaluatedValues) {
        finalHTML = finalHTML.replaceAll(`<<${key}>>`, evaluatedValues[key]);
      }

      // Add order number for debugging when SHOW_DB_PRICE_AND_MATCH is enabled
      if (SHOW_DB_PRICE_AND_MATCH) {
        finalHTML = `<span style="color:#999;font-size:10px;">[${order_place || 0}]</span> ${finalHTML}`;
      }

      // Calculate JS price (from w16_priceRules.js)
      let jsPrice = null;
      try {
        jsPrice = GetPrice({ templateName: doorData.quoteTemplate, dataId: id, doorData, templates, returnOnlyPrice: true });
      } catch (getPriceError) {
        console.error(`[ERROR] GetPrice failed for ${id}:`, getPriceError);
        jsPrice = { price: 0 };
      }

      // Calculate DB price using the same GetPrice but with dbPriceRulesState
      let dbPriceResult = null;
      try {
        dbPriceResult = dbPriceRulesState.length > 0
          ? GetPrice({ dataId: id, doorData, templates, returnOnlyPrice: true, dbPriceRules: dbPriceRulesState })
          : null;
      } catch (dbPriceError) {
        console.error(`[ERROR] GetPrice (DB) failed for ${id}:`, dbPriceError);
        dbPriceResult = null;
      }
      const dbPrice = dbPriceResult?.price ?? null;

      // Debug for top2_grillType
      if (id === 'top2_grillType') {
        console.log('[DEBUG DTR] top2_grillType dbPriceResult:', dbPriceResult);
        console.log('[DEBUG DTR] top2_grillType dbPrice:', dbPrice);
        console.log('[DEBUG DTR] top2_grillType jsPrice:', jsPrice);
      }

      // Use DB price or JS price based on PRICES_FROM_DB setting
      const price = PRICES_FROM_DB && dbPriceResult ? { ...dbPriceResult } : { ...jsPrice };

      if (upPrice > 0 && price.price > 0){
        price.price = price.price * (1 + upPrice / 100);
      }
 
      // Find if any promotion applies
      let matchedPromo = null;

      for (const promo of QuotePromotions) {
        if (!promo.visible) continue;

        const applies = promo.applies_to?.trim();
        if (!applies) continue;

        // Split multiple sub-rules by the custom delimiter "<>"
        const subRules = applies.split('<>').map(r => r.trim()).filter(Boolean);

        for (const rule of subRules) {
          if (rule.includes(':')) {
            const [key, value] = rule.split(':');

            // Normalize both sides minimally
            const normalizedKey = key.replace(/\s+/g, '').toLowerCase();
            const normalizedValue = value.replace(/\s+/g, '').toLowerCase();
            const normalizedHTML = finalHTML
              .replace(/<[^>]*>/g, '')
              .replace(/[\u2022\u2713\u2714✔]/g, '')
              .replace(/\s+/g, '')
              .toLowerCase();

            // Support wildcards like "GlassEnergy:*"
            if (
              id.replace(/\s+/g, '').toLowerCase() === normalizedKey &&
              (normalizedValue === '*' || normalizedHTML.includes(normalizedValue))
            ) {
              matchedPromo = promo;
              break;
            }
          } else {
            if (
              doorData?.quoteTemplate
                ?.replace(/\s+/g, '')
                .toLowerCase()
                .includes(rule.replace(/\s+/g, '').toLowerCase())
            ) {
              matchedPromo = promo;
              break;
            }
          }
        }

        if (matchedPromo) break;
      }

      // If promo matched, apply discount
      if (matchedPromo && typeof price.price === "number" && price.price > 0) {
        price.original = price.price;

        if (matchedPromo.discount_type === "%") {
          price.price = price.price * (1 - matchedPromo.discount / 100);
        } else if (matchedPromo.discount_type === "amount") {
          price.price = Math.max(price.price - matchedPromo.discount, 0);
        }
      }
  
      // Attach promo
      const promo = matchedPromo || {};
      // Store both JS and DB prices for comparison
      const jsPriceVal = jsPrice?.price ?? null;
      const priceRuleId = dbPriceResult?.priceRuleId ?? jsPrice?.priceRuleId ?? null;
      const priceSource = dbPriceResult?.priceSource ?? jsPrice?.priceSource ?? 'rules';
      lines[id] = { html: finalHTML, price, isVisible, promo, dbPrice, jsPrice: jsPriceVal, priceRuleId, priceSource };

    } catch (error) {
      console.error("Error rendering template:", error);
      return <p>Error rendering conditional template</p>;
    }
  };

  // Process templates
  const sortedTemplates = templates.filter(t => t.active).sort((a, b) => (a.order_place || 0) - (b.order_place || 0));
  sortedTemplates.forEach(renderTemplate);

  // Totals
  const visibleLines = Object.entries(lines)
                            .filter(([, val]) => val.isVisible)
                            .map(([id, val]) => [id, { ...val }]);

  // Calculate totals
  let totalSubtotal = 0;
  for (const key in lines) {
    const line = lines[key];
    const price = typeof line?.price?.price === 'number' ? line.price.price : 0;
    totalSubtotal += price;
  }

  const quantity = Number(doorData?.frameData?.quantity || 1);
  const subtotal = totalSubtotal * quantity;

  // Subtotal-only promos
  const subtotalOnlyPromos = QuotePromotions.filter(p => p.applies_to === 'subtotal_only');
  const hasSubtotalPromo = subtotalOnlyPromos.length > 0;

  let originalSubtotal = null;
  let discountedSubtotal = subtotal;

  if (hasSubtotalPromo) {
    originalSubtotal = subtotal;
    let temp = subtotal;
    for (const promo of subtotalOnlyPromos) {
      if (promo.discount_type === '%') {
        temp *= (1 - promo.discount / 100);
      } else {
        temp -= promo.discount;
      }
      temp = Math.max(temp, 0);
    }
    discountedSubtotal = temp;
  } else if (doorData.discounts.discount > 0) {
    discountedSubtotal = subtotal * (1 - doorData.discounts.discount / 100);
  }

  // Tax & total
  const tax = discountedSubtotal * 0.13;
  const total = discountedSubtotal + tax;

  // Update calc data
  const calcUpdate = {
    visibleLines: visibleLines,
    baseSubtotal: totalSubtotal.toFixed(2),
    discountedSubtotal: discountedSubtotal.toFixed(2),
    subtotal: subtotal.toFixed(2),
    tax: tax.toFixed(2),
    total: total.toFixed(2),
  };

  if (originalSubtotal !== null) {
    calcUpdate.originalSubtotal = originalSubtotal.toFixed(2);
  }

  const prevVisibleLines = JSON.stringify(doorData.calc.visibleLines || []);
  const newVisibleLines = JSON.stringify(visibleLines);

  if (
    doorData.calc.discountedSubtotal !== discountedSubtotal.toFixed(2) ||
    prevVisibleLines !== newVisibleLines ||
    doorData.calc.originalSubtotal !== calcUpdate.originalSubtotal
  ) {
    setTimeout(() => updateCalcData(calcUpdate), 0);
  }

  // Helper to check if prices match (within 1 cent tolerance)
  const pricesMatch = (jsPrice, dbPrice) => {
    if (typeof jsPrice !== 'number' || typeof dbPrice !== 'number') return null;
    return Math.abs(jsPrice - dbPrice) < 0.01;
  };

  return (
    <div className="template-renderer">
      <table border='0' className="price-table">
        {displayPrices && (
          <colgroup>
            <col style={{ width: SHOW_DB_PRICE_AND_MATCH ? '55%' : '90%' }} />
            <col style={{ width: SHOW_DB_PRICE_AND_MATCH ? '15%' : '10%' }} />
            {SHOW_DB_PRICE_AND_MATCH && <col style={{ width: '15%' }} />}
            {SHOW_DB_PRICE_AND_MATCH && <col style={{ width: '15%' }} />}
          </colgroup>
        )}
        <tbody>
          {/* Header row for price comparison */}
          {displayPrices && (
            <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', fontSize: '0.85em' }}>
              <td style={{ padding: '4px 8px' }}>Line Item</td>
              <td style={{ padding: '4px 8px', textAlign: 'right' }}>Price</td>
              {SHOW_DB_PRICE_AND_MATCH && (
                <td style={{ padding: '4px 8px', textAlign: 'right', color: '#0066cc' }}>{PRICES_FROM_DB ? 'JS Price' : 'DB Price'}</td>
              )}
              {SHOW_DB_PRICE_AND_MATCH && (
                <td style={{ padding: '4px 8px', textAlign: 'center' }}>Match</td>
              )}
            </tr>
          )}
          {visibleLines.map(([id, val], index) => {
            // First visible line is caption with colspan based on displayPrices
            if (index === 0) {
              return (
                <tr key={id}>
                  <td colSpan={displayPrices ? (SHOW_DB_PRICE_AND_MATCH ? 4 : 2) : 1} className="price-table-caption" dangerouslySetInnerHTML={{ __html: val.html }} />
                </tr>
              );
            }

            const hasPromo = val.promo?.id && ((val.price.original - val.price.price).toFixed(2) > 0);
            const savingsAmount = hasPromo ? (val.price.original - val.price.price).toFixed(2) : 0;

            // Build the complete text for the additional row (full price info with savings)
            const fullPriceText = hasPromo
              ? `$${val.price.original?.toFixed(2)} $${val.price.price.toFixed(2)} You saved $${savingsAmount} (${val.promo.discount_type === "%" ? `${val.promo.discount}% off` : `$${val.promo.discount} off`}!)`
              : null;

            // Price comparison - get JS and DB prices
            const jsPriceVal = typeof val.jsPrice === 'number' ? val.jsPrice : null;
            const dbPriceVal = typeof val.dbPrice === 'number' ? val.dbPrice : null;
            const matchStatus = pricesMatch(jsPriceVal, dbPriceVal);
            // Comparison column shows the opposite of the main price
            const comparisonPrice = PRICES_FROM_DB ? jsPriceVal : dbPriceVal;
            const comparisonLabel = PRICES_FROM_DB ? 'JS Price' : 'DB Price';

            return (
              <React.Fragment key={id}>
                <tr>
                  <td dangerouslySetInnerHTML={{ __html: val.html }} className="price-cell" />
                  {displayPrices && (
                    <td className="price-cell-value">
                      {hasPromo ? (
                        <span className="price-discounted" title={`Discounted: $${val.price.price.toFixed(2)}`}>
                          ${val.price.price.toFixed(2)}
                        </span>
                      ) : (
                        typeof val.price.price === 'number' && val.price.price > 0 ? (
                          <span className="price-regular" title={`Price: $${val.price.price.toFixed(2)}`}>
                            ${val.price.price.toFixed(2)}
                          </span>
                        ) : null
                      )}
                      {hasFeature('ALLOW_TO_SEE_PRICING_REFERENCE') && val.priceRuleId && typeof val.price.price === 'number' && val.price.price > 0 && (
                        <a
                          href={`/Setup?tab=pricing&pricingTab=${val.priceSource}&rule=${val.priceRuleId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Pricing Rule: ${val.priceRuleId}`}
                          style={{ marginLeft: 4, fontSize: '0.7em', color: '#aaa', textDecoration: 'none', verticalAlign: 'middle' }}
                        >
                          ⚙
                        </a>
                      )}
                    </td>
                  )}
                  {displayPrices && SHOW_DB_PRICE_AND_MATCH && (
                    <td className="price-cell-value" style={{ color: '#0066cc', fontSize: '0.9em' }}>
                      {comparisonPrice !== null && comparisonPrice > 0 ? (
                        <span title={`${comparisonLabel}: $${comparisonPrice.toFixed(2)}`}>
                          ${comparisonPrice.toFixed(2)}
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>--</span>
                      )}
                    </td>
                  )}
                  {displayPrices && SHOW_DB_PRICE_AND_MATCH && (
                    <td style={{ textAlign: 'center', fontSize: '0.9em' }}>
                      {matchStatus === true && <span style={{ color: '#28a745' }} title="Prices match">✓</span>}
                      {matchStatus === false && <span style={{ color: '#dc3545' }} title={`Diff: $${Math.abs((jsPriceVal || 0) - (dbPriceVal || 0)).toFixed(2)}`}>✗</span>}
                      {matchStatus === null && <span style={{ color: '#999' }}>-</span>}
                    </td>
                  )}
                </tr>
                {hasPromo && displayPrices && (
                  <tr className="savings-row">
                    <td colSpan={SHOW_DB_PRICE_AND_MATCH ? 4 : 2} className="savings-cell">
                      <small className="savings-text" title={fullPriceText}>
                        <span style={{ textDecoration: 'line-through', color: '#999' }}>
                          ${val.price.original?.toFixed(2)}
                        </span>
                        {' '}
                        ${val.price.price.toFixed(2)} You saved ${savingsAmount} (
                        {val.promo.discount_type === "%" ? `${val.promo.discount}% off` : `$${val.promo.discount} off`}!)
                      </small>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {displayPrices && (
            <>
              <tr><td colSpan={SHOW_DB_PRICE_AND_MATCH ? 4 : 2} className="table-spacer"></td></tr>
              <tr className="savings-row">
                <td colSpan={SHOW_DB_PRICE_AND_MATCH ? 4 : 2} className="savings-cell"><small>Price for 1 item:</small> <bold>${doorData.calc.baseSubtotal}</bold></td>
              </tr>
            </>
          )}
        </tbody>
      </table>
          
      {displayPrices && (
        <div className="price-summary">
          <p>Quantity: <span className="price-value-bold">{quantity}</span></p>
          
          <p className="price-summary-subtotal">
            <strong>Subtotal:</strong>{' '}
            <span className="price-value-bold">
              ${doorData.calc.discountedSubtotal}
            </span>
            {doorData.calc.originalSubtotal && (
              <>
                {' '}
                <s className="subtotal-original">
                  ${doorData.calc.originalSubtotal}
                </s>
                {' '}
                <span className="promo-description">
                  (
                  {subtotalOnlyPromos.map(p => {
                    const type = p.discount_type === '%' ? `${p.discount}%` : `$${p.discount}`;
                    return `${type} Off Subtotal${p.name ? ` – ${p.name}` : ''}`;
                  }).join(', ')}
                  )
                </span>
              </>
            )}
          </p>
          
          {/* Conditionally show After Discount line only if discount > 0 */}
          {doorData.discounts.discount > 0 && ((doorData.discounts.discount) > alertDiscount) && (
            <p>After Discount ({doorData.discounts.discount}%): <span className="price-value-alert">${doorData.calc.discountedSubtotal}</span></p>
          )}
          {doorData.discounts.discount > 0 && ((doorData.discounts.discount) <= alertDiscount) && (
            <p>After Discount ({doorData.discounts.discount}%): <span className="price-value-bold">${doorData.calc.discountedSubtotal}</span></p>
          )}          
          <p>Tax HST 13%: <span className="price-value-bold">${doorData.calc.tax}</span></p>
          <p>Total: <span className="price-value-bold">${doorData.calc.total}</span></p>
        </div>
      )}

      {/* Debug output - commented in original */}
      {/* <div className="debug-output">
        {JSON.stringify(lines, null, 2)}
      </div> */}
    </div>
  );
};

export default DynamicTemplateRenderer;